import { decodeWorkflowRun, type ChatMessage } from "@sagot/chat";
import { useTranslation } from "@sagot/i18n";
import { Button, ErrorState, Field, Icon, Select, Skeleton } from "@sagot/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useAgentList } from "../../shared/use-engage-data";
import type { Definition, PreviewStart } from "../types";
import { PreviewChat } from "./preview-chat";
import { usePreviewStart } from "./use-preview";

const P = "engage.workflows.builder.preview";

interface Session {
  conversationId: string;
  agentId: string;
  agentName: string;
  messages: ChatMessage[];
  awaitingMessage: boolean;
  /** A run was due to start (no message needed) but nothing matched. */
  notStarted: boolean;
}

function sessionOf(started: PreviewStart): Session {
  const { messages } = decodeWorkflowRun(started);
  return {
    conversationId: started.conversation_id,
    agentId: started.agent_id,
    agentName: started.agent_name,
    messages,
    awaitingMessage: started.awaiting_message,
    notStarted: !started.awaiting_message && started.run === null,
  };
}

function ChatSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t(`${P}.loading`)} className="space-y-3 rounded-lg bg-sunken p-3">
      <Skeleton className="h-10 w-3/4 rounded-lg" />
      <Skeleton className="h-8 w-1/2 rounded-pill" />
      <Skeleton className="h-8 w-2/5 rounded-pill" />
    </div>
  );
}

interface FacingPreviewProps {
  workflowId: string;
  definition: Definition;
}

/** Customer-facing workflows: a test conversation with the agent of your choice, started fresh each time. */
export function FacingPreview({ workflowId, definition }: FacingPreviewProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const agents = useAgentList();
  const start = usePreviewStart(workflowId);
  const [session, setSession] = useState<Session | null>(null);
  /** The agent asked for, then the one the server answered with (it picks when none is asked for). */
  const [agentId, setAgentId] = useState<string | undefined>(undefined);
  const began = useRef(false);

  const begin = useCallback(
    (chosen?: string) => {
      setSession(null);
      if (chosen) setAgentId(chosen);
      start.mutate(
        { agentId: chosen, definition },
        { onSuccess: (started) => { setAgentId(started.agent_id); setSession(sessionOf(started)); } },
      );
    },
    [definition, start],
  );
  useEffect(() => {
    if (began.current) return;
    began.current = true;
    begin();
  }, [begin]);

  const options = useMemo(() => {
    const limited = definition.settings.agent_ids;
    return (agents.data ?? []).filter((agent) => limited.length === 0 || limited.includes(agent.id) || agent.id === agentId);
  }, [agents.data, definition.settings.agent_ids, agentId]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-end gap-2">
        <Field label={t(`${P}.agent`)} hint={t(`${P}.agentHint`)} className="min-w-0 flex-1">
          <Select
            value={agentId ?? ""}
            disabled={start.isPending || options.length === 0}
            onChange={(e) => { begin(e.target.value); }}
          >
            {agentId ? null : <option value="" disabled />}
            {options.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
          </Select>
        </Field>
        <Button variant="secondary" disabled={start.isPending} onClick={() => { begin(agentId); }}>
          <Icon name="refresh-cw" />
          {t(`${P}.restart`)}
        </Button>
      </div>
      {start.isError ? (
        <ErrorState
          headingLevel={3}
          title={t(`${P}.failedTitle`)}
          description={message(start.error)}
          onRetry={() => { begin(agentId); }}
          retryLabel={t("common.retry")}
        />
      ) : session ? (
        <PreviewChat
          key={session.conversationId}
          workflowId={workflowId}
          agentId={session.agentId}
          agentName={session.agentName}
          seed={{ conversationId: session.conversationId, messages: session.messages }}
          awaitingMessage={session.awaitingMessage}
          notStarted={session.notStarted}
        />
      ) : (
        <ChatSkeleton />
      )}
    </div>
  );
}
